import { useEffect, useRef, useState } from "react";
import { Heart, ShoppingBag, ArrowUpRight, Coins } from "lucide-react";
import { API } from "../utils/constants";

const formatUsd = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

/**
 * Expected backend contract for the live feed (not built yet):
 *
 *   GET `${API}/merch/charity/transactions?limit=20`
 *   -> { transactions: [
 *        {
 *          id: string | number,
 *          type: "donation" | "purchase" | "payout",   // payout = money leaving the fund
 *          label: string,        // e.g. "Merch order #1042" / "Direct donation"
 *          source: string,       // e.g. "Store", "Stripe", "Twitch Bits"
 *          amount: number,       // always positive; sign is derived from `type`
 *          created_at: string,   // ISO timestamp
 *        }, ...
 *      ] }
 *
 * Until that endpoint exists, this component falls back to a small
 * simulated live feed below (clearly marked "Preview" instead of
 * "Live") so the layout and entrance animations can be previewed
 * end to end. Once the real endpoint is live, this switches over
 * automatically — no other changes needed here.
 */
const DEMO_MODE = true;
const MAX_ROWS = 8;

const DEMO_POOL = [
  { type: "donation", label: "Direct donation", source: "Stripe", min: 5, max: 60 },
  { type: "purchase", label: "Merch order", source: "Store", min: 12, max: 45 },
  { type: "donation", label: "Twitch Bits cheer", source: "Twitch", min: 2, max: 15 },
  { type: "purchase", label: "Sticker pack order", source: "Store", min: 6, max: 20 },
  { type: "donation", label: "Community donation", source: "PayPal", min: 10, max: 80 },
  { type: "payout", label: "Grant sent to Water.org", source: "Payout", min: 80, max: 200 },
];

function randomDemoTx() {
  const pick = DEMO_POOL[Math.floor(Math.random() * DEMO_POOL.length)];
  const amount = Math.round((pick.min + Math.random() * (pick.max - pick.min)) * 100) / 100;
  return {
    id: `demo-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
    type: pick.type,
    label: pick.type === "purchase" ? `${pick.label} #${1000 + Math.floor(Math.random() * 900)}` : pick.label,
    source: pick.source,
    amount,
    created_at: new Date().toISOString(),
  };
}

const SEED_DEMO = Array.from({ length: 5 }).map((_, i) => {
  const tx = randomDemoTx();
  tx.id = `seed-${i}`;
  tx.created_at = new Date(Date.now() - 1000 * 60 * (4 + i * 26)).toISOString();
  return tx;
});

const TYPE_META = {
  donation: { icon: Heart, label: "Donation", sign: "+" },
  purchase: { icon: ShoppingBag, label: "Purchase", sign: "+" },
  payout: { icon: ArrowUpRight, label: "Payout", sign: "-" },
};

function timeAgo(iso) {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function TransactionRow({ tx, isNew }) {
  const meta = TYPE_META[tx.type] || TYPE_META.donation;
  const Icon = meta.icon;
  const isOut = tx.type === "payout";

  return (
    <div className={`toa-ledger-row ${isNew ? "toa-ledger-row-new" : ""}`}>
      <div className={`toa-ledger-icon ${isOut ? "toa-ledger-icon-out" : "toa-ledger-icon-in"}`}>
        <Icon className="toa-ico" />
      </div>
      <div className="toa-ledger-main">
        <div className="toa-ledger-label">{tx.label}</div>
        <div className="toa-ledger-meta">
          {meta.label} · {tx.source} · {timeAgo(tx.created_at)}
        </div>
      </div>
      <div className={`toa-ledger-amount ${isOut ? "toa-ledger-amount-out" : "toa-ledger-amount-in"}`}>
        {meta.sign}{formatUsd(tx.amount)}
      </div>
    </div>
  );
}

function TransactionRowSkeleton() {
  return (
    <div className="toa-ledger-row toa-ledger-row-skel">
      <div className="toa-skel toa-skel-circle" />
      <div className="toa-ledger-main">
        <div className="toa-skel toa-skel-line" style={{ width: "55%" }} />
        <div className="toa-skel toa-skel-line" style={{ width: "35%", height: 9, marginTop: 7 }} />
      </div>
      <div className="toa-skel toa-skel-line" style={{ width: 55, height: 14 }} />
    </div>
  );
}

export default function CharityFundPage() {
  const [fundTotal, setFundTotal] = useState(0);
  const [displayTotal, setDisplayTotal] = useState(0);
  const displayedRef = useRef(0);
  const animationFrame = useRef(null);

  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(true);
  const [isLiveFeed, setIsLiveFeed] = useState(!DEMO_MODE);
  const [newIds, setNewIds] = useState(() => new Set());
  const knownIds = useRef(new Set());
  const transactionsRef = useRef([]);
  useEffect(() => { transactionsRef.current = transactions; }, [transactions]);

  // Fund total — polled every 30s, animated count-up
  useEffect(() => {
    let cancelled = false;

    async function loadTotal() {
      try {
        const response = await fetch(`${API}/merch/charity/total`, {
          headers: { Accept: "application/json" },
          credentials: "include",
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        const total = Number(data?.total || 0);
        if (!cancelled && Number.isFinite(total)) {
          setFundTotal(Math.max(0, total));
        }
      } catch (err) {
        console.error("[Charity Fund]", err);
      }
    }

    loadTotal();
    const interval = window.setInterval(loadTotal, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    const from = displayedRef.current;
    const to = fundTotal;
    const duration = 1500;
    const startedAt = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const value = from + (to - from) * eased;
      displayedRef.current = value;
      setDisplayTotal(value);
      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        displayedRef.current = to;
        setDisplayTotal(to);
      }
    };

    animationFrame.current = requestAnimationFrame(animate);
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, [fundTotal]);

  // Mark newly-arrived rows so they get the entrance/highlight animation,
  // then clear the highlight after it's played.
  function applyIncoming(list) {
    const fresh = list.filter((tx) => !knownIds.current.has(tx.id));
    list.forEach((tx) => knownIds.current.add(tx.id));

    setTransactions(list.slice(0, MAX_ROWS));

    if (fresh.length) {
      setNewIds((prev) => {
        const next = new Set(prev);
        fresh.forEach((tx) => next.add(tx.id));
        return next;
      });
      window.setTimeout(() => {
        setNewIds((prev) => {
          const next = new Set(prev);
          fresh.forEach((tx) => next.delete(tx.id));
          return next;
        });
      }, 2300);
    }
  }

  // Real transaction feed — polled every 30s
  useEffect(() => {
    let cancelled = false;

    async function loadTransactions() {
      try {
        const response = await fetch(`${API}/merch/charity/transactions?limit=${MAX_ROWS}`, {
          headers: { Accept: "application/json" },
          credentials: "include",
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        const list = Array.isArray(data?.transactions) ? data.transactions : [];

        if (!cancelled) {
          setIsLiveFeed(true);
          setTxLoading(false);
          applyIncoming(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[Charity Fund] transactions", err);
          if (DEMO_MODE) {
            setIsLiveFeed(false);
            setTxLoading(false);
            applyIncoming(SEED_DEMO);
          } else {
            setIsLiveFeed(false);
            setTxLoading(false);
            setTransactions([]);
          }
        }
      }
    }

    loadTransactions();
    const interval = window.setInterval(loadTransactions, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  // Demo-only: drip in a fresh simulated transaction every so often so the
  // "new transaction" animation is visible while there's no real backend yet.
  useEffect(() => {
    if (!DEMO_MODE) return;

    const interval = window.setInterval(() => {
      setIsLiveFeed((live) => {
        if (live) return live; // real backend took over — stop simulating
        applyIncoming([randomDemoTx(), ...transactionsRef.current]);
        return live;
      });
    }, 9000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <main className="toa-charity-page">
      <section className="toa-charity-hero">
        <div className="toa-charity-glow toa-charity-glow-a" />
        <div className="toa-charity-glow toa-charity-glow-b" />

        <div className="toa-charity-hero-inner">
          <h1>Charity Fund</h1>
          <p className="toa-charity-sub">
            A community fund dedicated to turning collective support into
            meaningful help for people and causes that need it.
          </p>

          <div className="toa-charity-total">{formatUsd(displayTotal)}</div>
          <div className="toa-charity-total-caption">raised so far, together</div>
        </div>
      </section>

      <section className="toa-ledger-flow">
        <div className="toa-ledger-head">
          <span className={`toa-ledger-dot ${isLiveFeed ? "" : "toa-ledger-dot-off"}`} />
          <span className="toa-ledger-head-label">
            {isLiveFeed ? "Live Activity" : "Activity Preview"}
          </span>
        </div>

        {!isLiveFeed && !txLoading && (
          <div className="toa-ledger-demo-note">
            Example activity shown — the live feed connects once it's wired up on the backend.
          </div>
        )}

        <div className="toa-ledger-list">
          {txLoading && (
            <>
              <TransactionRowSkeleton />
              <TransactionRowSkeleton />
              <TransactionRowSkeleton />
            </>
          )}

          {!txLoading && transactions.length === 0 && (
            <div className="toa-ledger-empty">
              <Coins className="toa-ico-xl" />
              <div>No transactions yet — check back soon.</div>
            </div>
          )}

          {!txLoading && transactions.map((tx) => (
            <TransactionRow key={tx.id} tx={tx} isNew={newIds.has(tx.id)} />
          ))}
        </div>
      </section>
    </main>
  );
}